import * as React from 'react';
import {
    getRibbonBottomBarId,
    RibbonBottomBar,
    RibbonTabDefinitionBase,
    RibbonTabDefinitionSet,
    RibbonTopBar,
    RibbonTabDefinitionSLR,
    RibbonTabDefinitionMLR,
} from '@1js/acui-ribbon-like/lib/UISurfaces/Ribbon/components';
import { getDefaultRibbonControlRendererFunction } from '@1js/acui-ribbon-like/lib/UISurfaces/Ribbon/getDefaultRibbonControlRendererFunction';
import { getDefaultRibbonControlInMenuRendererFunction } from '@1js/acui-ribbon-like/lib/UISurfaces/Ribbon/getDefaultRibbonControlInMenuRendererFunction';
import { isMac } from '@1js/acui-common';
import { AppFeatureManager, AppWindowProvider } from '@1js/acui-context/lib/components/AppContext';
import { AppTooltipManager } from '@1js/acui-tooltip/lib/components/AppTooltipManager';
import { observer } from 'mobx-react-lite';
import { getIsViewModeSelected } from 'owa-command-ribbon-store';
import { CommandingViewMode } from 'owa-outlook-service-option-store/lib/store/schema/options/CommandingOptions';
import { getCurrentRibbonTheme } from '../util/getCurrentRibbonTheme';
import { getRibbonExperiments } from '../util/getRibbonExperiments';
import { LazyCommandingModeMenu } from 'owa-commanding-mode-menu';
import { getIsDarkTheme } from 'owa-fabric-theme';
import { SvgIconStyleProvider } from 'owa-fluent-icons-svg';
import { getIsMsHighContrast } from 'owa-high-contrast';
import { KeyCodes, ThemeProvider } from '@fluentui/react';
import type { IKeytipTransitionKey } from '@fluentui/react';
import { KeytipLayer } from '@fluentui/react/lib/KeytipLayer';
import { format, memoizeFunction } from '@fluentui/react/lib/Utilities';
import styles from './CommandRibbon.scss';
import classnamesBind from 'classnames/bind';
import { isFeatureEnabled } from 'owa-feature-flags';
import { isHostAppFeatureEnabled } from 'owa-hostapp-feature-flags';
import { IconColorMap, initializeIconColorMap } from '../util/getRibbonIconColor';
import { RibbonId, getStringRibbonId } from 'owa-ribbon-ids';
import { useOrchestrator } from 'owa-react-hooks/lib/useOrchestrator';
import { invokeNewEventByKeyboard } from '../actions/invokeNewEventByKeyboard';
import { appIcons, getAppStrings, getAppColors, ensureAppThemeInitialized } from 'owa-acui-theme';

const classNames = classnamesBind.bind(styles);

const svgRootStyle = {
    verticalAlign: 'top',
    height: '100%',
    width: '100%',
};

const svgIconStyle = {
    maxWidth: '28px',
};

// Keytip start sequences
const monarchKeytipsKeys: string[] = ['Alt', 'F10'];

const getKeytipContent = memoizeFunction((): string => {
    const keytipStartSequence: string = 'Alt {0}';
    const keytipStartKeyWindows: string = 'Windows';
    const keytipStartKeyMac: string = 'Command';
    const keytipAltMetaStartSequence: string = format(
        keytipStartSequence,
        isMac() ? keytipStartKeyMac : keytipStartKeyWindows
    );

    return isHostAppFeatureEnabled('altForKeytips')
        ? `${keytipAltMetaStartSequence} / ${monarchKeytipsKeys[1]}`
        : keytipAltMetaStartSequence;
});

const getKeytipEntrySequences = memoizeFunction((): IKeytipTransitionKey[] => {
    if (isHostAppFeatureEnabled('altForKeytips')) {
        /** Uncomment the following when Alt is enabled in Monarch for keytips
        const monarchKeytipsTransitionKeys: IKeytipTransitionKey[] = [];

        monarchKeytipsKeys.forEach(keyString => {
            monarchKeytipsTransitionKeys.push({ key: keyString });
        });

        return monarchKeytipsTransitionKeys;
        */

        return [{ key: 'Meta', modifierKeys: [KeyCodes.alt] }, { key: monarchKeytipsKeys[1] }];
    }

    return [{ key: 'Meta', modifierKeys: [KeyCodes.alt] }];
});

const paddleProps = {
    disableModeTogglePadding: true,
    ribbonPaddlesIcon: appIcons.downChevron.icon12,
};

export interface CommandRibbonProps {
    getSingleLineRibbonDefinition: () => RibbonTabDefinitionSet;
    getMultiLineRibbonDefinition: () => RibbonTabDefinitionSet;
    iconColorMap: IconColorMap;
    isProjectionPopout?: boolean; // ADO135671 - For OWA MSIT Experiment
    getDefaultSelectedTabId?: () => RibbonId;
    onTabClick?: (tabId: string) => void;
    getHideTopRibbon?: () => boolean;
    getExtraTopRibbonControls?: () => JSX.Element[];
    getExtraBottomRibbonControls?: () => JSX.Element[];
    onNewEvent?: () => void;
    onRibbonUpdate?: () => void;
    thisWindow?: Window;
}

export default observer(function CommandRibbon(props: CommandRibbonProps) {
    React.useEffect(() => props.onRibbonUpdate?.());
    useOrchestrator(invokeNewEventByKeyboard, props.onNewEvent, []);

    // In order for dark/light mode toggle to be respected, this needs
    // to be called before the first render, so no useEffect.
    // Inside render so that it doesn't get called unless we actually render the Ribbon.
    // Only needs to be called once, so ensure pattern is used here.
    ensureAppThemeInitialized();

    // Initial rendering will occur when the ribbon definitions are created, so we
    // need to initialize the icon color map first.
    initializeIconColorMap(props.iconColorMap);

    const defaultSelectedTabId = props.getDefaultSelectedTabId
        ? getStringRibbonId(props.getDefaultSelectedTabId())
        : '';
    const [selectedTabId, setSelectedTabId] = React.useState(defaultSelectedTabId);

    const isToolbarMode: boolean = getIsViewModeSelected(CommandingViewMode.CommandBar);
    const isCommandbarMode: boolean = isToolbarMode && !isFeatureEnabled('mon-ribbon-toolbar');
    const theWindow: Window = props.thisWindow ?? window;

    React.useEffect(() => {
        setSelectedTabId(defaultSelectedTabId);
    }, [defaultSelectedTabId]);

    React.useEffect(() => {
        if (!isCommandbarMode && isHostAppFeatureEnabled('altForKeytips')) {
            theWindow.addEventListener('keydown', preventDfltOnKeytipKeys);

            return () => {
                theWindow.removeEventListener('keydown', preventDfltOnKeytipKeys);
            };
        } else {
            return () => {};
        }
    }, []);

    if (isCommandbarMode) {
        return null;
    }

    const getSelectedTab = (
        tabs: RibbonTabDefinitionBase[]
    ): RibbonTabDefinitionSLR | RibbonTabDefinitionMLR => {
        const matchingTabs: RibbonTabDefinitionBase[] = tabs.filter(
            tab => tab.id === selectedTabId
        );

        // If there is no match for the tab, return the first tab
        const matchingTab: RibbonTabDefinitionBase =
            matchingTabs.length === 0 ? tabs[0] : matchingTabs[0];

        if (matchingTabs.length === 0) {
            onTabHeaderClick(matchingTab.id);
        }

        return matchingTab.type === 'SingleLine'
            ? (matchingTab as RibbonTabDefinitionSLR)
            : (matchingTab as RibbonTabDefinitionMLR);
    };

    const onTabHeaderClick = (tabId: string) => {
        if (props.onTabClick) {
            props.onTabClick(tabId);
        }

        setSelectedTabId(tabId);
    };

    const ribbonTabs = getIsViewModeSelected(CommandingViewMode.MultiLineRibbon)
        ? props.getMultiLineRibbonDefinition()
        : props.getSingleLineRibbonDefinition();

    const selectedTab: RibbonTabDefinitionSLR | RibbonTabDefinitionMLR = getSelectedTab(
        ribbonTabs.tabs
    );

    const isHighContrast = getIsMsHighContrast();
    const isDarkTheme = !isHighContrast && getIsDarkTheme();
    const rbnStyle = getIsViewModeSelected(CommandingViewMode.SingleLineRibbon)
        ? styles.singleLine
        : isToolbarMode
        ? styles.commandBar
        : undefined;
    const rbnContainerStyle = isToolbarMode
        ? styles.ribbonToolbarContainer
        : styles.ribbonContainer;

    const controlInMenuRendererFunction = getDefaultRibbonControlInMenuRendererFunction(
        appIcons,
        getAppStrings()
    );

    return (
        <div className={styles.root} style={{ flexDirection: 'column' }}>
            {!props.getHideTopRibbon?.() && (
                <div id="TopBar" style={{ display: 'flex' }}>
                    <div className={rbnContainerStyle}>
                        <AppFeatureManager isExperimentalFeatureEnabled={getRibbonExperiments}>
                            <ThemeProvider theme={getCurrentRibbonTheme()}>
                                <AppWindowProvider appWindow={theWindow}>
                                    <AppTooltipManager>
                                        <RibbonTopBar
                                            appColors={getAppColors()}
                                            appStrings={getAppStrings()}
                                            displayDynamicTabHeaderIndicator={true}
                                            onTabHeaderClick={onTabHeaderClick}
                                            overflowTabHeaderMenuLauncherIcon={
                                                appIcons.tabListOverflowLauncher
                                            }
                                            ribbonTabs={ribbonTabs.tabs}
                                            selectedTabId={selectedTab.id}
                                        />
                                    </AppTooltipManager>
                                </AppWindowProvider>
                            </ThemeProvider>
                        </AppFeatureManager>
                    </div>
                    {props.getExtraTopRibbonControls?.()}
                </div>
            )}
            <div id="BottomBar" style={{ display: 'flex' }}>
                <div className={rbnContainerStyle}>
                    <AppFeatureManager isExperimentalFeatureEnabled={getRibbonExperiments}>
                        <SvgIconStyleProvider rootStyle={svgRootStyle} svgStyle={svgIconStyle}>
                            <ThemeProvider theme={getCurrentRibbonTheme()}>
                                <AppWindowProvider appWindow={theWindow}>
                                    <AppTooltipManager>
                                        <RibbonBottomBar
                                            appStrings={getAppStrings()}
                                            ariaLabeledBy={selectedTab.id}
                                            controlGroups={selectedTab.controlGroups}
                                            controlInMenuRendererFunction={
                                                controlInMenuRendererFunction
                                            }
                                            controlRendererFunction={getDefaultRibbonControlRendererFunction(
                                                appIcons,
                                                controlInMenuRendererFunction
                                            )}
                                            id={getRibbonBottomBarId(selectedTab.id)}
                                            overflowMenuLauncherIcon={appIcons.overflowMenuLauncher}
                                            initialScalingSteps={selectedTab.initialScalingSteps}
                                            scalingSteps={selectedTab.scalingSteps}
                                            paddleProps={paddleProps}
                                        />
                                    </AppTooltipManager>
                                </AppWindowProvider>
                            </ThemeProvider>
                        </SvgIconStyleProvider>
                    </AppFeatureManager>
                </div>
                {props.getExtraBottomRibbonControls?.()}
                <div
                    className={classNames(
                        styles.modeMenuContainer,
                        rbnStyle,
                        isDarkTheme && styles.darkTheme
                    )}>
                    <LazyCommandingModeMenu />
                </div>
            </div>
            {
                /* ADO135671 - For OWA MSIT Experiment, we want to disable Keytips in popped out windows. */
                !props.isProjectionPopout && (
                    <KeytipLayer
                        content={getKeytipContent()}
                        keytipExitSequences={getKeytipEntrySequences()}
                        keytipStartSequences={getKeytipEntrySequences()}
                    />
                )
            }
        </div>
    );
});

/**
 * In Monarch, we need to override Alt and F10. Otherwise, focus goes to the app chrome
 */
function preventDfltOnKeytipKeys(event: KeyboardEvent) {
    if (monarchKeytipsKeys.includes(event.key)) {
        event.preventDefault();
    }
}
